import React, { useState, useEffect } from 'react';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import { Table, Card, Typography, Space, Avatar, Tag, Button, Input, Select, DatePicker, Row, Col, Badge, Pagination, Tooltip, message, Modal, Form, Divider, Statistic, Radio, InputNumber, Alert } from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  ClearOutlined, 
  EyeOutlined,
  UserOutlined,
  CarOutlined,
  ShopOutlined,
  DollarOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  FileTextOutlined,
  PhoneOutlined,
  MailOutlined,
  TagOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  GoldOutlined,
  TeamOutlined,
  BoxPlotOutlined,
  RiseOutlined,
  PercentageOutlined,
  CheckOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import API from '../../API/API';
import './YardAssignmentModal.css';
import dayjs from 'dayjs';
import YardContactFields from '../LeadDetails/components/YardContactFields';
import {
    buildYardContactDraft,
    normalizeYardContactValue
} from '../LeadDetails/utils/yardContactOptions';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import { YardModal } from '../LeadDetails/LeadDetails';
import YardReassignmentHistoryModal from '../../Components/YardReassignmentHistory/YardReassignmentHistoryModal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const toPST = (date) => {
  if (!date) return null;
  return dayjs(date).tz('America/Los_Angeles');
};

const normalizeYardId = (yardId) => {
    if (!yardId) return null;
    if (typeof yardId === 'object') {
        return yardId._id || yardId.id || null;
    }
    return yardId;
};

const MAX_YARD_ASSIGNMENTS = 3;

const getAssignmentYardAddressText = (yardAddress) => {
    if (typeof yardAddress === 'string') {
        return yardAddress.trim();
    }

    if (!yardAddress) {
        return '';
    }

    return [
        yardAddress.address,
        yardAddress.street,
        yardAddress.city,
        yardAddress.state,
        yardAddress.zip_code
    ].filter(Boolean).join(', ');
};

const buildAssignmentYardAddress = (yardAddress) => ({
    address: getAssignmentYardAddressText(yardAddress),
    street: '',
    city: '',
    state: '',
    country: '',
    zip_code: ''
});

const getYardDraftAddressText = (yardData = {}, yardDetails = {}) => {
    return getAssignmentYardAddressText(yardData.yardAddress) ||
        getAssignmentYardAddressText(yardDetails.yardAddress);
};

const calculateYardFinalPrice = (yardDraft = {}) => {
    return (Number(yardDraft.price) || 0) +
        (Number(yardDraft.yardShipping) || 0) +
        (Number(yardDraft.ownShipping) || 0) +
        (Number(yardDraft.extraWarrantyPrice) || 0) +
        (Number(yardDraft.boxingPalleteFee) || 0) +
        (Number(yardDraft.coreCharges) || 0);
};

const buildYardDraft = (yardData = {}, yardDetails = {}) => ({
    price: Number(yardData.price) || 0,
    originalPrice: Number(yardData.originalPrice) || 0,
    description: yardData.description || '',
    yardShipping: Number(yardData.yardShipping) || 0,
    ownShipping: Number(yardData.ownShipping) || 0,
    coreCharges: Number(yardData.coreCharges) || 0,
    extraWarrantyPrice: Number(yardData.extraWarrantyPrice) || 0,
    boxingPalleteFee: Number(yardData.boxingPalleteFee) || 0,
    ccChargesPercentage: Number(yardData.ccChargesPercentage) || 0,
    yardWarranty: yardData.yardWarranty || '',
    yardMiles: yardData.yardMiles || '',
    ...buildYardContactDraft(yardData, yardDetails),
    yardAddress: getYardDraftAddressText(yardData, yardDetails),
    yardConfirmDate: yardData.yardConfirmDate ? dayjs(yardData.yardConfirmDate) : dayjs()
});

const getLatestYardSelection = (yardEntries = []) => {
    const items = Array.isArray(yardEntries) ? yardEntries.filter(Boolean) : [];

    if (items.length === 0) {
        return {
            latestSelectionGroupId: null,
            currentGroupYards: [],
            currentYard: null,
            currentYardIndex: -1,
            comparisonYards: [],
            previousYards: []
        };
    }

    const currentIndices = items.reduce((result, yard, index) => {
        if (yard?.isCurrent === true) {
            result.push(index);
        }
        return result;
    }, []);
    const currentYardIndex = currentIndices.length > 0 ? currentIndices[currentIndices.length - 1] : items.length - 1;
    const currentYard = items[currentYardIndex] || items[items.length - 1];

    return {
        latestSelectionGroupId: currentYard?.selectionGroupId || null,
        currentGroupYards: currentYard ? [currentYard] : [],
        currentYard,
        currentYardIndex,
        comparisonYards: [],
        previousYards: items.filter((_, index) => index !== currentYardIndex)
    };
};

const findExistingYardRecord = (yardEntries = [], yardId) => {
    const normalizedYardId = normalizeYardId(yardId);
    if (!normalizedYardId) {
        return null;
    }

    for (let index = yardEntries.length - 1; index >= 0; index -= 1) {
        const yard = yardEntries[index];
        if (normalizeYardId(yard?.yardId) === normalizedYardId) {
            return yard;
        }
    }

    return null;
};

const getYardDisplayName = (yardDetails, yardRecord) => {
    if (yardDetails?.yardName) return yardDetails.yardName;
    if (typeof yardRecord?.yardId === 'object' && yardRecord?.yardId?.yardName) {
        return yardRecord.yardId.yardName;
    }
    const fallbackId = normalizeYardId(yardRecord?.yardId);
    return fallbackId ? `Yard ${fallbackId}` : 'Unnamed Yard';
};

const getPrimaryYardValue = (value) => {
    if (Array.isArray(value)) {
        return value[0] || null;
    }
    return value || null;
};



const ReLocate = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
  // The search a request uses: the box's text, set 0.5 s after typing pauses
  // (at once when the box is cleared), so a search is not sent per keystroke.
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const trimmed = searchText.trim();
    if (!trimmed) {
      setSearchQuery('');
      return undefined;
    }
    const timer = setTimeout(() => setSearchQuery(trimmed), 500);
    return () => clearTimeout(timer);
  }, [searchText]);
  const [searchFields, setSearchFields] = useSearchFields('relocate:search-fields');
  // The "Search in" group only changes the results of a search, so changing it
  // with an empty search box sends no request.
  const effectiveSearchFields = searchQuery ? searchFields : null;
  const [relocateFilter, setRelocateFilter] = useState(null);
  const [quickFilter, setQuickFilter] = useState('thisMonth'); // Default to this month
  const [dateRange, setDateRange] = useState(() => {
    const now = dayjs();
    return [now.startOf('month'), now.endOf('month')];
  });
  const [yardDetailsMap, setYardDetailsMap] = useState({});
  const [loginAgent, setLoginAgent] = useState({});
  
  // Edit modal states
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('No');
  const [selectedDate, setSelectedDate] = useState(dayjs());

const [yardModalVisible, setYardModalVisible] = useState(false);
const [selectedLeadForYard, setSelectedLeadForYard] = useState(null);
const [yardHistoryLead, setYardHistoryLead] = useState(null);

// Add these with your other state declarations
const [agents, setAgents] = useState([]); // Sales agents
const [serviceAgentsMap, setServiceAgentsMap] = useState({});
const [loadingServiceAgents, setLoadingServiceAgents] = useState(false);
// Edit Total Amount Modal States
const [isTotalAmountModalVisible, setIsTotalAmountModalVisible] = useState(false);
const [editingTotalAmount, setEditingTotalAmount] = useState(0);
const [selectedLeadForAmount, setSelectedLeadForAmount] = useState(null);
const [savingTotalAmount, setSavingTotalAmount] = useState(false);
 
const [editingSpecification, setEditingSpecification] = useState(null);
const [editingSpecValue, setEditingSpecValue] = useState('');
const [updatingSpecification, setUpdatingSpecification] = useState(false);
 

const handleUpdateSpecification = async (leadId, newSpecification) => {
    if (!leadId) return;
    
    const trimmedValue = newSpecification?.trim() || '';
    
    if (trimmedValue.length > 2000) {
        message.warning('Specification cannot exceed 2000 characters');
        return;
    }
    
    setUpdatingSpecification(true);
    try {
        // First, get the current lead data to preserve other vehicleInfo fields
        const currentLead = leads.find(lead => lead._id === leadId);
        const currentVehicleInfo = currentLead?.vehicleInfo || {};
        
        // Merge the specification with existing vehicleInfo
        const payload = {
            vehicleInfo: {
                ...currentVehicleInfo, // Preserve all existing fields
                specification: trimmedValue // Update only specification
            }
        };
        
        console.log('Sending payload with preserved data:', payload);
        
        const response = await API.UpdateLead(leadId, payload);
        
        if (response?.status) {
            message.success('Specification updated successfully!');
            fetchLeads(); // Refresh the leads list
        } else {
            message.error(response?.message || 'Failed to update specification');
        }
    } catch (error) {
        console.error('Error updating specification:', error);
        message.error('Failed to update specification');
    } finally {
        setUpdatingSpecification(false);
        setEditingSpecification(null);
        setEditingSpecValue('');
    }
};
useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (!userData) {
        navigate('/login');
        return;
    }
    setLoginAgent(JSON.parse(userData));
    
    // Fetch agents only for admin
    if (JSON.parse(userData)?.role === 'ADMIN') {
        fetchAgents();
    }
    fetchServiceAgents();
}, [navigate]);

  const isAdmin = loginAgent?.role === 'ADMIN';

  // Function to get date range based on quick filter
  const getDateRangeFromFilter = (filter) => {
    const now = dayjs();
    switch (filter) {
      case 'today':
        return [now.startOf('day'), now.endOf('day')];
      case 'yesterday':
        return [now.subtract(1, 'day').startOf('day'), now.subtract(1, 'day').endOf('day')];
      case 'thisWeek':
        return [now.startOf('week'), now.endOf('week')];
      case 'thisMonth':
        return [now.startOf('month'), now.endOf('month')];
      case 'lastMonth':
        return [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')];
      case 'thisQuarter':
        return [now.startOf('quarter'), now.endOf('quarter')];
      case 'thisYear':
        return [now.startOf('year'), now.endOf('year')];
      case 'custom':
        return dateRange; // Return existing custom range
      default:
        return [now.startOf('month'), now.endOf('month')];
    }
  };
const calculateTotalPaid = (leadData) => {
    if (!leadData?.advancePayments) return 0;
    return leadData.advancePayments.reduce((sum, payment) => {
        if (payment.status === 'Completed' || payment.status === undefined) {
            return sum + (Number(payment.amount) || 0);
        }
        return sum;
    }, 0);
};
// Fetch Sales Agents
const fetchAgents = async () => {
    try {
        const response = await API.userGetAll();
        if (response?.success) {
            const salesAgents = (response.data || []).filter(user => user.role === "SALES-AGENT");
            setAgents(salesAgents);
        }
    } catch (error) {
        console.error("Failed to fetch agents", error);
    }
};

// Fetch Service/Support Agents
const fetchServiceAgents = async () => {
    setLoadingServiceAgents(true);
    try {
        const response = await API.userGetAll();
        if (response?.success) {
            const agentsMap = {};
            const serviceAgentsList = (response.data || []).filter(
                user => user.role === "SERVICE-AGENT" || user.role === "SUPPORT-AGENT"
            );
            serviceAgentsList.forEach(agent => {
                agentsMap[agent._id] = {
                    name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.userName || agent.email,
                    email: agent.email,
                    phone: agent.directNumber,
                    role: agent.role
                };
            });
            setServiceAgentsMap(agentsMap);
        }
    } catch (error) {
        console.error("Failed to fetch service agents", error);
    } finally {
        setLoadingServiceAgents(false);
    }
};

// Get Sales Agent Name (existing)
const getAgentName = (record) => {
    if (record.assignedAgent?.name) return record.assignedAgent.name;
    if (record.agentName) return record.agentName;
    if (record.handledBy?.firstName && record.handledBy?.lastName) {
        return `${record.handledBy.firstName} ${record.handledBy.lastName}`;
    }
    if (record.handledBy?.name) return record.handledBy.name;
    return 'Unassigned';
};

// Get Service Agent Name (serviceHandledBy)
const getServiceAgentName = (record) => {
    const serviceAgentId = record.serviceHandledBy?._id || record.serviceHandledBy;
    if (!serviceAgentId) return null;
    
    if (record.serviceHandledBy && typeof record.serviceHandledBy === 'object') {
        return `${record.serviceHandledBy.firstName || ''} ${record.serviceHandledBy.lastName || ''}`.trim() || 
               record.serviceHandledBy.userName || 'Service Agent';
    }
    
    const agentInfo = serviceAgentsMap[serviceAgentId];
    return agentInfo?.name || null;
};

// Get Order Service Agent Name (orderServiceHandledBy)
const getOrderServiceAgentName = (record) => {
    const orderServiceAgentId = record.orderServiceHandledBy?._id || record.orderServiceHandledBy;
    if (!orderServiceAgentId) return null;
    
    if (record.orderServiceHandledBy && typeof record.orderServiceHandledBy === 'object') {
        return `${record.orderServiceHandledBy.firstName || ''} ${record.orderServiceHandledBy.lastName || ''}`.trim() || 
               record.orderServiceHandledBy.userName || 'Order Service Agent';
    }
    
    const agentInfo = serviceAgentsMap[orderServiceAgentId];
    return agentInfo?.name || null;
};

// Get Zoho Service Agent Name (zohoServiceHandledBy)
const getZohoServiceAgentName = (record) => {
    const zohoServiceAgentId = record.zohoServiceHandledBy?._id || record.zohoServiceHandledBy;
    if (!zohoServiceAgentId) return null;
    
    if (record.zohoServiceHandledBy && typeof record.zohoServiceHandledBy === 'object') {
        return `${record.zohoServiceHandledBy.firstName || ''} ${record.zohoServiceHandledBy.lastName || ''}`.trim() || 
               record.zohoServiceHandledBy.userName || 'Zoho Service Agent';
    }
    
    const agentInfo = serviceAgentsMap[zohoServiceAgentId];
    return agentInfo?.name || null;
};

// Get Yard Service Agent Name (yardServiceHandledBy)
const getYardServiceAgentName = (record) => {
    const yardServiceAgentId = record.yardServiceHandledBy?._id || record.yardServiceHandledBy;
    if (!yardServiceAgentId) return null;
    
    if (record.yardServiceHandledBy && typeof record.yardServiceHandledBy === 'object') {
        return `${record.yardServiceHandledBy.firstName || ''} ${record.yardServiceHandledBy.lastName || ''}`.trim() || 
               record.yardServiceHandledBy.userName || 'Yard Service Agent';
    }
    
    const agentInfo = serviceAgentsMap[yardServiceAgentId];
    return agentInfo?.name || null;
};

// Handle Re-Assign Yard
const handleReAssignYard = (record) => {
  setSelectedLeadForYard(record);
  setYardModalVisible(true);
};

// Handle Yard Modal Close
const handleYardModalClose = () => {
  setYardModalVisible(false);
  setSelectedLeadForYard(null);
};

// Handle Yard Modal Success
const handleYardModalSuccess = () => {
  setYardModalVisible(false);
  setSelectedLeadForYard(null);
  fetchLeads(); // Refresh the table to show updated yard info
  message.success('Yard reassigned successfully');
};

  // Handle quick filter change
  const handleQuickFilterChange = (value) => {
    setQuickFilter(value);
    if (value !== 'custom') {
      const newRange = getDateRangeFromFilter(value);
      setDateRange(newRange);
    }
  };

  // Handle custom date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      setQuickFilter('custom');
    } else {
      setDateRange(getDateRangeFromFilter('thisMonth'));
      setQuickFilter('thisMonth');
    }
  };
// Handle Edit Total Amount
const handleEditTotalAmount = (record) => {
    setSelectedLeadForAmount(record);
    setEditingTotalAmount(record.totalAmount || 0);
    setIsTotalAmountModalVisible(true);
};

// Handle Save Total Amount
const handleSaveTotalAmount = async () => {
    if (!editingTotalAmount || editingTotalAmount <= 0) {
        message.error('Please enter a valid amount');
        return;
    }
    
    if (!selectedLeadForAmount) return;
    
    setSavingTotalAmount(true);
    try {
        const response = await API.UpdateLead(selectedLeadForAmount._id, { 
            totalAmount: Number(editingTotalAmount)
        });
        
        if (response?.status || response?.success) {
            message.success('Total amount updated successfully');
            setIsTotalAmountModalVisible(false);
            setSelectedLeadForAmount(null);
            fetchLeads(); // Refresh the table data
        } else {
            message.error(response?.message || 'Failed to update total amount');
        }
    } catch (error) {
        console.error('Error updating total amount:', error);
        message.error('Failed to update total amount');
    } finally {
        setSavingTotalAmount(false);
    }
};
  // Fetch leads
  const fetchLeads = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        sortBy: 'relocateStatusUpdatedAt',
        order: 'desc',
        relocate:'Yes'
      };
      
    
      
      // Add search if present
      if (searchQuery) {
        params.search = searchQuery;
        params.searchFields = searchFields;
      }
      
      // Add date range filter
      const effectiveDateRange =
        dateRange && dateRange[0] && dateRange[1]
          ? dateRange
          : getDateRangeFromFilter('thisMonth');

      if (effectiveDateRange && effectiveDateRange[0] && effectiveDateRange[1]) {
        params.dateField = 'relocateStatusUpdatedDate';
        params.startDate = effectiveDateRange[0].startOf('day').toISOString();
        params.endDate = effectiveDateRange[1].endOf('day').toISOString();
      }
      
      console.log('API Params:', params);
      
      // The server sends only the fields this page uses
      // (LeadController LEAD_VIEW_FIELDS['relocate']).
      params.view = 'relocate';
      const response = await API.leadGetAll(params);
      
      if (response?.status && response.data) {
        setLeads(response.data);
        setTotalRecords(response.pagination?.totalRecords || response.data.length);
      } else {
        setLeads([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      message.error('Failed to fetch leads');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch yard details
  const fetchYardDetails = async (yardId) => {
    if (!yardId || yardDetailsMap[yardId]) return;
    
    try {
      const response = await API.GetYardById(yardId);
      if (response?.status || response?.success) {
        const yardData = response.data || response;
        setYardDetailsMap(prev => ({
          ...prev,
          [yardId]: yardData
        }));
      }
    } catch (error) {
      console.error('Error fetching yard details:', error);
    }
  };

  // Load yard details for leads
  useEffect(() => {
    const fetchAllYardDetails = async () => {
      const yardIds = new Set();
      
      leads.forEach(lead => {
        const yards = lead.yard || [];
        const latestYard = yards.length > 0 ? yards[yards.length - 1] : null;
        if (latestYard?.yardId && !yardDetailsMap[latestYard.yardId]) {
          yardIds.add(latestYard.yardId);
        }
      });
      
      for (const yardId of yardIds) {
        await fetchYardDetails(yardId);
      }
    };
    
    if (leads.length > 0) {
      fetchAllYardDetails();
    }
  }, [leads]);

  useEffect(() => {
    fetchLeads();
  }, [currentPage, pageSize, relocateFilter, searchQuery, effectiveSearchFields, dateRange]);

  // Handle relocate status update
  const handleUpdateRelocate = async () => {
    if (!selectedLead) return;
    
    setUpdating(true);
    try {
      const payload = {
        relocateStatus: selectedStatus,
        relocateStatusUpdatedAt: selectedDate.toISOString()
      };
      
      const response = await API.UpdateLead(selectedLead._id, payload);
      
      if (response?.status) {
        message.success(`Relocate status updated to ${selectedStatus === 'Yes' ? 'Relocated' : 'Not Relocated'}`);
        setModalVisible(false);
        setSelectedLead(null);
        fetchLeads();
      } else {
        message.error(response?.message || 'Failed to update');
      }
    } catch (error) {
      console.error('Error updating relocate status:', error);
      message.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  const openUpdateModal = (record) => {
    setSelectedLead(record);
    setSelectedStatus(record.relocateStatus || 'No');
    setSelectedDate(record.relocateStatusUpdatedAt ? dayjs(record.relocateStatusUpdatedAt) : dayjs());
    setModalVisible(true);
  };

  const handleView = (record) => {
    navigate(`/lead-details/${record._id}`);
  };

  const handleRefresh = () => {
    fetchLeads(true);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setRelocateFilter(null);
    setQuickFilter('thisMonth');
    setDateRange(getDateRangeFromFilter('thisMonth'));
    setCurrentPage(1);
    message.info('Filters cleared');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Get latest yard
  const getLatestYard = (record) => {
    const yards = record.yard || [];
    return yards.length > 0 ? yards[yards.length - 1] : null;
  };

const handleViewYardDetails = (record) => {
  navigate(`/lead-details/${record._id}`, {
    state: {
      sourceTab: 'order-process',
      activeTab: 'yard'
    }
  });
};
  // Statistics
  const relocatedCount = leads.filter(l => l.relocateStatus === 'Yes').length;
  const notRelocatedCount = leads.filter(l => l.relocateStatus !== 'Yes').length;
  const totalAmount = leads.reduce((sum, l) => sum + (l.totalAmount || 0), 0);
const columns = [
    {
        title: 'Order No',
        key: 'orderNumber',
        width: 120,
        render: (_, record) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileTextOutlined style={{ color: '#722ed1' }} />
                <Text strong style={{ color: '#722ed1', fontFamily: 'monospace' }}>
                    #{record.orderNumber?.slice(-8) || 'N/A'}
                </Text>
            </div>
        )
    },
    // SALES AGENT COLUMN
    {
        title: 'Sales Agent',
        key: 'salesAgent',
        width: 180,
        sorter: (a, b) => getAgentName(a).localeCompare(getAgentName(b)),
        render: (_, record) => {
            const agentName = getAgentName(record);
            const isUnassigned = agentName === 'Unassigned';
            return (
                <Tooltip title={agentName}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<UserOutlined />}
                            style={{
                                backgroundColor: isUnassigned ? '#d9d9d9' : '#722ed1',
                                flexShrink: 0,
                            }}
                        >
                            {!isUnassigned && agentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <Text
                            ellipsis={{ tooltip: true }}
                            style={{
                                fontSize: 13,
                                color: isUnassigned ? '#8c8c8c' : '#1f2937',
                                fontStyle: isUnassigned ? 'italic' : 'normal',
                            }}
                        >
                            {agentName}
                        </Text>
                    </div>
                </Tooltip>
            );
        },
    },
    // SERVICE AGENT 1: serviceHandledBy
    {
        title: 'Shipping Service-Agent',
        key: 'serviceAgent',
        width: 180,
        sorter: (a, b) => {
            const nameA = getServiceAgentName(a) || '';
            const nameB = getServiceAgentName(b) || '';
            return nameA.localeCompare(nameB);
        },
        render: (_, record) => {
            const serviceAgentName = getServiceAgentName(record);
            const isAssigned = !!serviceAgentName;
            
            return (
                <Tooltip title={isAssigned ? serviceAgentName : 'No service agent assigned'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: isAssigned ? '#52c41a' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {isAssigned && serviceAgentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <Text
                                    ellipsis={{ tooltip: true }}
                                    style={{
                                        fontSize: 13,
                                        color: '#1f2937',
                                        display: 'block',
                                        fontWeight: 500
                                    }}
                                >
                                    {serviceAgentName}
                                </Text>
                            ) : (
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: '#bfbfbf',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    Not Assigned
                                </Text>
                            )}
                        </div>
                    </div>
                </Tooltip>
            );
        },
    },
    // SERVICE AGENT 2: orderServiceHandledBy
    {
        title: 'Order Service-Agent',
        key: 'orderServiceAgent',
        width: 180,
        sorter: (a, b) => {
            const nameA = getOrderServiceAgentName(a) || '';
            const nameB = getOrderServiceAgentName(b) || '';
            return nameA.localeCompare(nameB);
        },
        render: (_, record) => {
            const agentName = getOrderServiceAgentName(record);
            const isAssigned = !!agentName;
            
            return (
                <Tooltip title={isAssigned ? agentName : 'No order service agent assigned'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: isAssigned ? '#1890ff' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {isAssigned && agentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <Text
                                    ellipsis={{ tooltip: true }}
                                    style={{
                                        fontSize: 13,
                                        color: '#1f2937',
                                        display: 'block',
                                        fontWeight: 500
                                    }}
                                >
                                    {agentName}
                                </Text>
                            ) : (
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: '#bfbfbf',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    Not Assigned
                                </Text>
                            )}
                        </div>
                    </div>
                </Tooltip>
            );
        },
    },
    // SERVICE AGENT 3: zohoServiceHandledBy
    {
        title: 'Doc Service-Agent',
        key: 'zohoServiceAgent',
        width: 180,
        sorter: (a, b) => {
            const nameA = getZohoServiceAgentName(a) || '';
            const nameB = getZohoServiceAgentName(b) || '';
            return nameA.localeCompare(nameB);
        },
        render: (_, record) => {
            const agentName = getZohoServiceAgentName(record);
            const isAssigned = !!agentName;
            
            return (
                <Tooltip title={isAssigned ? agentName : 'No Zoho service agent assigned'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: isAssigned ? '#faad14' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {isAssigned && agentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <Text
                                    ellipsis={{ tooltip: true }}
                                    style={{
                                        fontSize: 13,
                                        color: '#1f2937',
                                        display: 'block',
                                        fontWeight: 500
                                    }}
                                >
                                    {agentName}
                                </Text>
                            ) : (
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: '#bfbfbf',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    Not Assigned
                                </Text>
                            )}
                        </div>
                    </div>
                </Tooltip>
            );
        },
    },
    // SERVICE AGENT 4: yardServiceHandledBy
    {
        title: 'Yard Service-Agent',
        key: 'yardServiceAgent',
        width: 180,
        sorter: (a, b) => {
            const nameA = getYardServiceAgentName(a) || '';
            const nameB = getYardServiceAgentName(b) || '';
            return nameA.localeCompare(nameB);
        },
        render: (_, record) => {
            const agentName = getYardServiceAgentName(record);
            const isAssigned = !!agentName;
            
            return (
                <Tooltip title={isAssigned ? agentName : 'No yard service agent assigned'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: isAssigned ? '#eb2f96' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {isAssigned && agentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <Text
                                    ellipsis={{ tooltip: true }}
                                    style={{
                                        fontSize: 13,
                                        color: '#1f2937',
                                        display: 'block',
                                        fontWeight: 500
                                    }}
                                >
                                    {agentName}
                                </Text>
                            ) : (
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: '#bfbfbf',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    Not Assigned
                                </Text>
                            )}
                        </div>
                    </div>
                </Tooltip>
            );
        },
    },
    // CUSTOMER COLUMN
    {
        title: 'Customer',
        key: 'customer',
        width: 250,
        render: (_, record) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar icon={<UserOutlined />} style={{ background: '#1890ff' }} />
                <div>
                    <Text strong>{record.personalInfo?.fullName || 'N/A'}</Text>
                    <div style={{ fontSize: 12 }}>
                        <Space size={8}>
                            <span><MailOutlined /> {record.personalInfo?.email || 'N/A'}</span>
                            <span><PhoneOutlined /> {record.personalInfo?.phoneNumber || 'N/A'}</span>
                        </Space>
                    </div>
                </div>
            </div>
        )
    },
    // VEHICLE COLUMN
    {
        title: 'Vehicle',
        key: 'vehicle',
        width: 220,
        render: (_, record) => (
            <div>
                <Space>
                    <CarOutlined style={{ color: '#faad14' }} />
                    <Text strong>
                        {record.vehicleInfo?.year} {record.vehicleInfo?.make} {record.vehicleInfo?.model}
                    </Text>
                </Space>
                <div style={{ marginTop: 4 }}>
                    <Tag color="cyan" icon={<TagOutlined />}>
                        {record.vehicleInfo?.part || 'N/A'}
                    </Tag>
                </div>
            </div>
        )
    },
          {
    title: 'Specification',
    key: 'specification',
    width: 250,
    render: (_, record) => {
        const specification = record.vehicleInfo?.specification || '';
        const isEditing = editingSpecification === record._id;
        
        if (isEditing) {
            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={editingSpecValue}
                        onChange={(e) => setEditingSpecValue(e.target.value)}
                        placeholder="Enter specification details..."
                        style={{ flex: 1, fontSize: '12px' }}
                        autoFocus
                        maxLength={2000}
                        showCount
                    />
                    <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={updatingSpecification}
                            onClick={() => handleUpdateSpecification(record._id, editingSpecValue)}
                            style={{ minWidth: '32px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setEditingSpecification(null);
                                setEditingSpecValue('');
                            }}
                        />
                    </div>
                </div>
            );
        }
        
        return (
            <div 
                style={{ 
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    transition: 'all 0.3s',
                    backgroundColor: specification ? 'transparent' : '#fafafa',
                    border: '1px solid transparent',
                    minHeight: '60px'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = specification ? 'transparent' : '#fafafa';
                    e.currentTarget.style.borderColor = 'transparent';
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setEditingSpecification(record._id);
                    setEditingSpecValue(specification);
                }}
            >
                {specification ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <FileTextOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>Specification:</Text>
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            display: 'block',
                            lineHeight: '1.5'
                        }}>
                            {specification.length > 100 
                                ? `${specification.substring(0, 100)}...` 
                                : specification}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <EditOutlined style={{ marginRight: '4px' }} />
                                Click to edit
                            </Text>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <FileTextOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                            <EditOutlined style={{ marginRight: '4px', fontSize: '10px' }} />
                            Click to add specification
                        </Text>
                    </div>
                )}
            </div>
        );
    },
}, 
    // YARD COLUMN
  {
      title: 'Yard',
      key: 'yard',
      width: 200, // Increased width to accommodate verification info
      render: (_, record) => {
          const latestYard = record.yard?.length > 0 
              ? record.yard[record.yard.length - 1] 
              : null;
          const yardDetails = latestYard?.yardId 
              ? yardDetailsMap[latestYard.yardId] 
              : null;
  
          if (!latestYard) {
              return (
                  <div>
                      <Space>
                          <Text type="secondary">No yard assigned</Text>
                          <Tooltip title="View Yard Details">
                              <Button
                                  type="text"
                                  size="small"
                                  shape="circle"
                                  icon={<EyeOutlined />}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewYardDetails(record);
                                  }}
                              />
                          </Tooltip>
                      </Space>
                      <div style={{ marginTop: 8 }}>
                          <Button
                              type="link"
                              size="small"
                              icon={<ShopOutlined />}
                              onClick={() => handleReAssignYard(record)}
                              style={{ padding: 0 }}
                          >
                              Assign Yard
                          </Button>
                      </div>
                  </div>
              );
          }
  
          const isVerified = latestYard.verifiedAmountStats === 'YES';
          const verifiedPrice = latestYard.verifiedAmountPrice || 0;
          const finalPrice = latestYard.finalPrice || latestYard.price || 0;
          const priceDifference = verifiedPrice - finalPrice;
          const hasDifference = priceDifference !== 0;
  
          return (
              <div>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space>
                          <ShopOutlined style={{ color: '#722ed1' }} />
                          <Text strong>
                              {yardDetails?.yardName || 'Loading...'}
                          </Text>
                          <Tooltip title="View Yard Details">
                              <Button
                                  type="text"
                                  size="small"
                                  shape="circle"
                                  icon={<EyeOutlined />}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewYardDetails(record);
                                  }}
                              />
                          </Tooltip>
                      </Space>
                      
                      <div style={{ fontSize: 12 }}>
                          <div>
                              💰 Price: ${finalPrice.toFixed(2)}
                          </div>
                          {latestYard.yardConfirmDate && (
                              <div>
                                  📅 {dayjs(latestYard.yardConfirmDate).format('MM/DD/YYYY')}
                              </div>
                          )}
                      </div>
  
                      {/* Verification Status Display */}
                      <div style={{ marginTop: '4px' }}>
                          {isVerified ? (
                              <div>
                                  <Space size={4} wrap>
                                      <Tag color="success" size="small" style={{ fontSize: '11px', margin: 0 }}>
                                          <CheckCircleOutlined /> Verified
                                      </Tag>
                                      {hasDifference ? (
                                          <Tag 
                                              color={priceDifference > 0 ? 'orange' : 'red'} 
                                              style={{ fontSize: '11px', margin: 0 }}
                                          >
                                              {priceDifference > 0 ? '▲' : '▼'} ${Math.abs(priceDifference).toFixed(2)} diff
                                          </Tag>
                                      ) : (
                                          <Tag color="green" style={{ fontSize: '11px', margin: 0 }}>
                                              ✓ Matches
                                          </Tag>
                                      )}
                                      <Tooltip title={`Verified: $${verifiedPrice.toFixed(2)} | Final: $${finalPrice.toFixed(2)} | Difference: ${priceDifference > 0 ? '+' : ''}${priceDifference.toFixed(2)}`}>
                                          <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '12px', cursor: 'pointer' }} />
                                      </Tooltip>
                                  </Space>
                                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                      ✓ ${verifiedPrice.toFixed(2)}
                                  </div>
                              </div>
                          ) : (
                              <Tag color="warning" size="small" style={{ fontSize: '11px' }}>
                                  <ClockCircleOutlined /> Not Verified
                              </Tag>
                          )}
                      </div>
  
                      <Space direction="vertical" size={2} style={{ alignItems: 'flex-start' }}>
                          <Button
                              type="link"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleReAssignYard(record)}
                              style={{ padding: 0, fontSize: 12 }}
                          >
                              Re-assign Yard
                          </Button>
                          {record.yardReassignmentHistory?.length > 0 && (
                              <Tooltip title="View yard reassignment history">
                                  <Button
                                      type="link"
                                      size="small"
                                      icon={<HistoryOutlined />}
                                      onClick={() => setYardHistoryLead(record)}
                                      style={{ padding: 0, fontSize: 12 }}
                                  >
                                      History ({record.yardReassignmentHistory.length})
                                  </Button>
                              </Tooltip>
                          )}
                      </Space>
                  </Space>
              </div>
          );
      }
  },
    // TOTAL AMOUNT COLUMN
    {
        title: 'Total Amount',
        key: 'totalAmount',
        width: 140,
        sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
        render: (_, record) => (
            <>
                <Tag color="green" style={{ fontSize: 16, fontWeight: 'bold', padding: '4px 12px' }}>
                    {formatCurrency(record.totalAmount || 0)}
                </Tag>
                <Button
                    type="default"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => handleEditTotalAmount(record)}
                    title="Edit Total Amount"
                    style={{ borderColor: '#1890ff', color: '#1890ff' }}
                />
            </>
        )
    },
    // RELOCATE STATUS COLUMN
    {
        title: 'Relocate Status',
        key: 'relocateStatus',
        width: 160,
        render: (_, record) => {
            const isRelocated = record.relocateStatus === 'Yes';
            const updatedAt = record.relocateStatusUpdatedAt;
            
            return (
                <div>
                    <Tag 
                        color={isRelocated ? 'purple' : 'default'}
                        icon={isRelocated ? <CheckCircleOutlined /> : <ReloadOutlined />}
                        style={{ padding: '4px 12px', fontSize: 12 }}
                    >
                        {isRelocated ? '✓ Relocated' : '✗ Not Relocated'}
                    </Tag>
                    {updatedAt && (
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                            <ClockCircleOutlined /> {dayjs(updatedAt).format('MM/DD/YY hh:mm A')}
                        </div>
                    )}
                </div>
            );
        }
    },
    // ACTIONS COLUMN
    {
        title: 'Actions',
        key: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
            <Space>
                <Tooltip title="View Details">
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        size="small"
                        shape="circle"
                        onClick={() => handleView(record)}
                    />
                </Tooltip>
                <Tooltip title="Edit Relocate Status">
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        shape="circle"
                        onClick={() => openUpdateModal(record)}
                        style={{ background: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                    />
                </Tooltip>
            </Space>
        )
    }
];
  return (
    <div style={{ padding: 24, minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <Card style={{ marginBottom: 24, borderRadius: 20 }}>
        <Row align="middle" gutter={16}>
          <Col>
            <Avatar 
              size={64} 
              icon={<ReloadOutlined />} 
              style={{ background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)' }}
            />
          </Col>
          <Col flex="auto">
            <Title level={2} style={{ margin: 0 }}>Relocate Management</Title>
            <Text type="secondary">Track and manage vehicle relocation status</Text>
          </Col>
        </Row>
      </Card>


   
      {/* Filters */}
      <Card style={{ marginBottom: 24, borderRadius: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Space.Compact block>
              <SearchFieldsSelect value={searchFields} onChange={setSearchFields} />
              <Input
                placeholder={getSearchFieldsPlaceholder(searchFields)}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                size="large"
              />
            </Space.Compact>
          </Col>
       
          <Col xs={24} md={5}>
            <Select
              value={quickFilter}
              onChange={handleQuickFilterChange}
              size="large"
              style={{ width: '100%', borderRadius: 12 }}
            >
              <Option value="today">Today</Option>
              <Option value="yesterday">Yesterday</Option>
              <Option value="thisWeek">This Week</Option>
              <Option value="thisMonth">This Month</Option>
              <Option value="lastMonth">Last Month</Option>
              <Option value="thisQuarter">This Quarter</Option>
              <Option value="thisYear">This Year</Option>
              <Option value="custom">Custom Range</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <RangePicker
              style={{ width: '100%', borderRadius: 12 }}
              onChange={handleDateRangeChange}
              value={dateRange}
              size="large"
              format="MM/DD/YYYY"
              placeholder={['Start Date', 'End Date']}
            />
          </Col>
          <Col xs={24} md={5}>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                loading={refreshing}
                size="large"
                style={{ borderRadius: 12 }}
              >
                Refresh
              </Button>
              <Button 
                icon={<ClearOutlined />} 
                onClick={handleClearFilters}
                size="large"
                style={{ borderRadius: 12 }}
              >
                Clear
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Results count */}
      <div style={{ padding: '12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Badge count={leads.length} showZero style={{ backgroundColor: '#1890ff' }} />
          <Text type="secondary">orders found</Text>
        </Space>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalRecords}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          onChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
          size="small"
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
        />
      </div>

      {/* Main Table */}
      <Card style={{ borderRadius: 16, overflow: 'hidden' }} bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={leads}
          rowKey="_id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1300 }}
          rowClassName={() => 'table-row-hover'}
        />
        
        {/* Bottom Pagination */}
        {leads.length > 0 && (
          <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'center', background: '#fafafa' }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              showSizeChanger
              pageSizeOptions={["10", "20", "50", "100"]}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              showQuickJumper
              showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} orders`}
            />
          </div>
        )}
      </Card>

      {/* Update Relocate Modal */}
      <Modal
        title={
          <Space>
            <ReloadOutlined style={{ color: '#722ed1', fontSize: 20 }} />
            <Text strong style={{ fontSize: 18 }}>Update Relocate Status</Text>
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedLead(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setModalVisible(false);
            setSelectedLead(null);
          }}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleUpdateRelocate}
            loading={updating}
            icon={<SaveOutlined />}
          >
            Update Status
          </Button>
        ]}
        width={500}
        centered
      >
        {selectedLead && (
          <div>
            {/* Customer & Vehicle Info */}
            <Card size="small" style={{ marginBottom: 20, borderRadius: 12, background: '#f5f5f5' }}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong>{selectedLead.personalInfo?.fullName}</Text>
                <Text type="secondary">
                  {selectedLead.vehicleInfo?.year} {selectedLead.vehicleInfo?.make} {selectedLead.vehicleInfo?.model}
                </Text>
                <Text type="secondary">Part: {selectedLead.vehicleInfo?.part}</Text>
                <Divider style={{ margin: '8px 0' }} />
                <Text strong>Total Amount: {formatCurrency(selectedLead.totalAmount || 0)}</Text>
                <div>
                  <Text type="secondary">Current Status: </Text>
                  <Tag color={selectedLead.relocateStatus === 'Yes' ? 'purple' : 'default'}>
                    {selectedLead.relocateStatus === 'Yes' ? 'Relocated' : 'Not Relocated'}
                  </Tag>
                </div>
              </Space>
            </Card>
            
            <Form layout="vertical">
              <Form.Item label="Relocate Status" required>
                <Select 
                  value={selectedStatus} 
                  onChange={setSelectedStatus} 
                  size="large"
                  style={{ borderRadius: 8 }}
                >
                  <Option value="Yes">
                    <Space>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      <span>Yes - Relocated</span>
                    </Space>
                  </Option>
                  <Option value="No">
                    <Space>
                      <CloseCircleOutlined style={{ color: '#f5222d' }} />
                      <span>No - Not Relocated</span>
                    </Space>
                  </Option>
                </Select>
              </Form.Item>
              
              <Form.Item label="Status Date" required>
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  style={{ width: '100%', borderRadius: 8 }}
                  size="large"
                  showTime={{ format: 'hh:mm A' }}
                  format="MM/DD/YYYY hh:mm A"
                />
              </Form.Item>
            </Form>
            
            <Alert
              message="Note"
              description="Updating the relocate status will record the timestamp of when this change was made."
              type="info"
              showIcon
              style={{ marginTop: 16, borderRadius: 8 }}
            />
          </div>
        )}
      </Modal>
{/* Edit Total Amount Modal */}
<Modal
    title={
        <Space>
            <EditOutlined style={{ color: '#1890ff' }} />
            <span>Edit Total Amount</span>
        </Space>
    }
    open={isTotalAmountModalVisible}
    onCancel={() => {
        setIsTotalAmountModalVisible(false);
        setSelectedLeadForAmount(null);
    }}
    footer={[
        <Button key="cancel" onClick={() => {
            setIsTotalAmountModalVisible(false);
            setSelectedLeadForAmount(null);
        }}>
            Cancel
        </Button>,
        <Button 
            key="submit" 
            type="primary" 
            loading={savingTotalAmount}
            onClick={handleSaveTotalAmount}
            icon={<SaveOutlined />}
        >
            Update Total Amount
        </Button>
    ]}
    width={500}
    centered
>
    {selectedLeadForAmount && (
        <div>
            {/* Customer & Vehicle Info */}
            <Card size="small" style={{ marginBottom: 20, borderRadius: 12, background: '#f5f5f5' }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text strong>{selectedLeadForAmount.personalInfo?.fullName || 'N/A'}</Text>
                    <Text type="secondary">
                        {selectedLeadForAmount.vehicleInfo?.year} {selectedLeadForAmount.vehicleInfo?.make} {selectedLeadForAmount.vehicleInfo?.model}
                    </Text>
                    <Text type="secondary">Part: {selectedLeadForAmount.vehicleInfo?.part || 'N/A'}</Text>
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">Current Paid Amount: </Text>
                        <Text strong style={{ color: '#52c41a' }}>
                            {formatCurrency(calculateTotalPaid(selectedLeadForAmount))}
                        </Text>
                    </div>
                    <div>
                        <Text type="secondary">Current Balance: </Text>
                        <Text strong style={{ color: '#fa8c16' }}>
                            {formatCurrency((selectedLeadForAmount.totalAmount || 0) - calculateTotalPaid(selectedLeadForAmount))}
                        </Text>
                    </div>
                </Space>
            </Card>
            
            <Form layout="vertical">
                <Form.Item label="Current Total Amount">
                    <InputNumber
                        value={selectedLeadForAmount.totalAmount}
                        disabled
                        style={{ width: '100%' }}
                        prefix="$"
                        step={0.01}
                        precision={2}
                    />
                </Form.Item>

                <Form.Item 
                    label="New Total Amount" 
                    required
                    extra="Changing total amount will automatically update the balance"
                >
                    <InputNumber
                        value={editingTotalAmount}
                        onChange={setEditingTotalAmount}
                        style={{ width: '100%' }}
                        prefix="$"
                        min={0.01}
                        step={0.01}
                        precision={2}
                        placeholder="Enter new total amount"
                        autoFocus
                    />
                </Form.Item>

                <Alert
                    message="Balance will be updated"
                    description={`New balance will be: ${formatCurrency(
                        (editingTotalAmount || 0) - calculateTotalPaid(selectedLeadForAmount)
                    )}`}
                    type="info"
                    showIcon
                />
            </Form>
        </div>
    )}
</Modal>
{/* Re-Assign Yard Modal */}
{selectedLeadForYard && (
  <YardModal
    visible={yardModalVisible}
    onCancel={handleYardModalClose}
    onOk={handleYardModalSuccess}
    lead={selectedLeadForYard}
  />
)}

<YardReassignmentHistoryModal
  open={!!yardHistoryLead}
  onClose={() => setYardHistoryLead(null)}
  history={yardHistoryLead?.yardReassignmentHistory}
  leadId={yardHistoryLead?._id}
  yards={yardHistoryLead?.yard}
  yardDetailsMap={yardDetailsMap}
  leadName={yardHistoryLead?.personalInfo?.fullName}
  orderNumber={yardHistoryLead?.orderNumber}
  onNotesChanged={() => fetchLeads()}
/>
      <style>{`
        .table-row-hover:hover {
          background: linear-gradient(90deg, #f0f7ff 0%, #ffffff 100%) !important;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f0f0f0 !important;
        }
        
        .ant-table-thead > tr > th {
          background: #fafafa !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          color: #1f2937 !important;
          border-bottom: 2px solid #f0f0f0 !important;
        }
      `}</style>
    </div>
  );
};

export default ReLocate;
